import { useEffect, useState, useMemo, useRef } from "react";
import { CircularProgress } from "@mui/material";
import CopyToClipboard from "react-copy-to-clipboard";

import PrimaryButton from "../../../Components/Buttons/primary-button";
import CloseButton from "../../../Components/Buttons/close-button";
import CustomStepper from "../../../Components/Others/custom-stepper";
import SkeletonLoader from "../../../Components/Skeleton/skeleton-loader";

import renderStakeCheckingOnboardingStep from "../Stake Checking Status/stake-checking-onboarding-step";
import renderGetBankAmountDetails from "../Add Funds/get-bank-amount-details";

import useConnectBankAccount from "../../../CustomHooks/useConnectBankAccount";

import { useConnectedAccounts, useDeals, useRentDetails, useStakeDebit } from "../../../CustomHooks/Queries";

import { useModalContext } from "../../../Contexts/ModalContext";
import { useViewport } from "../../../Contexts/ViewportContext";

import { mediumScreenStartFrom, modalVariants, nilYearLongMonthDateFormat } from "../../../Utils/constants";

import {
  formatCurrency,
  isSVDApproved,
  isSVDPending,
  isSVDEligible,
  isSVDCardRequested,
  isSVDDebitCardActivated,
  isSVDDenied,
  dispatchSuccessToast,
  hasSVDPhysicalCardArrived,
  formatDate,
  appendISOTime,
} from "../../../Utils/helpers";

import { StakeCardFrontSide } from "../../../Assets/Images";

import "./setup-stake-pay-2.scss";

const stakePay2SetupStatuses = {
  steps1: "steps-1",
  steps2: "steps-2",
  steps3: "steps-3",
};

const screenStates = {
  landing: "landing",
  step1: "step-1",
  step2: "step-2",
  step3: "step-3",
};

const BackChevronIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path d="M15 19L8 12L15 5" stroke="#6C7086" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const CopyIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
    <rect x="4.5" y="4.5" width="8" height="8" rx="1.5" stroke="#9398AB" strokeWidth="1.2" />
    <path d="M9.5 4.5V3C9.5 2.17 8.83 1.5 8 1.5H3C2.17 1.5 1.5 2.17 1.5 3V8C1.5 8.83 2.17 9.5 3 9.5H4.5" stroke="#9398AB" strokeWidth="1.2" />
  </svg>
);

const GreenCheckIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
    <path d="M3.5 9.5L7 13L14.5 5.5" stroke="var(--accent)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const trackerStepLabels = ["Open Stake\nChecking", "Fund your\naccount", "Update your\npayment portal"];

export default function renderSetupStakePay2(modalContext, defaultStep = stakePay2SetupStatuses.steps1, props) {
  const { updateModalComponent, pushModalStack, setModalVariant, setModalClassName, setModalStep, setModalAriaLabel } = modalContext;

  updateModalComponent(() => <SetupStakePay2 defaultStep={defaultStep} {...props} />);
  pushModalStack(() => renderSetupStakePay2(modalContext, defaultStep, props));
  setModalVariant(modalVariants.stepByStepDialog);
  setModalClassName("setup-stake-pay-2");
  setModalStep(Object.values(stakePay2SetupStatuses).findIndex((key) => key === defaultStep) + 1);
  setModalAriaLabel?.("Set up Stake Pay");
}

const PayRentSkeleton = () => {
  const skeletonSx = { bgcolor: "var(--loader-bg-dark-tertiary)" };
  return (
    <div className="pr-modal-skeleton">
      <div className="pr-skel-header">
        <SkeletonLoader variant="circular" width={32} height={32} sx={skeletonSx} />
        <SkeletonLoader width={180} height={20} className="border--rounded-full" sx={skeletonSx} />
        <SkeletonLoader variant="circular" width={32} height={32} sx={skeletonSx} />
      </div>
      <div className="pr-skel-tracker">
        <div className="pr-skel-tracker-dots">
          <SkeletonLoader variant="circular" width={10} height={10} sx={skeletonSx} />
          <SkeletonLoader width="100%" height={2} sx={skeletonSx} />
          <SkeletonLoader variant="circular" width={10} height={10} sx={skeletonSx} />
          <SkeletonLoader width="100%" height={2} sx={skeletonSx} />
          <SkeletonLoader variant="circular" width={10} height={10} sx={skeletonSx} />
        </div>
        <div className="pr-skel-tracker-labels">
          <SkeletonLoader width={80} height={10} className="border--rounded-full" sx={skeletonSx} />
          <SkeletonLoader width={80} height={10} className="border--rounded-full" sx={skeletonSx} />
          <SkeletonLoader width={80} height={10} className="border--rounded-full" sx={skeletonSx} />
        </div>
      </div>
      <SkeletonLoader width="100%" height={1} sx={skeletonSx} />
      <div className="pr-skel-body">
        <SkeletonLoader width="60%" height={24} className="border--rounded-full" sx={skeletonSx} />
        <SkeletonLoader width="90%" height={16} className="border--rounded-full" sx={skeletonSx} />
        <SkeletonLoader width="75%" height={16} className="border--rounded-full" sx={skeletonSx} />
      </div>
      <SkeletonLoader width="100%" height={48} className="border--rounded-lg" sx={skeletonSx} />
    </div>
  );
};

const StepHeader = ({ onBack, onClose, backDisabled }) => (
  <div className="pr-custom-header">
    <button className="pr-back-btn" onClick={onBack} disabled={backDisabled} aria-label="Go back">
      <BackChevronIcon />
    </button>
    <span className="pr-header-title">Pay Rent with Stake</span>
    <CloseButton onClick={onClose} className="pr-close-btn" />
  </div>
);

const Tracker = ({ currentStep, completedStep }) => (
  <div
    className="pr-tracker"
    role="group"
    aria-label={`Pay rent setup, step ${currentStep} of ${trackerStepLabels.length}`}
  >
    <div className="pr-tracker-dots" aria-hidden="true">
      <div className={`pr-tracker-dot${currentStep >= 1 || completedStep >= 1 ? " active" : ""}`} />
      <div className={`pr-tracker-line${currentStep >= 2 || completedStep >= 2 ? " active" : ""}`} />
      <div className={`pr-tracker-dot${currentStep >= 2 || completedStep >= 2 ? " active" : ""}`} />
      <div className={`pr-tracker-line${currentStep >= 3 || completedStep >= 3 ? " active" : ""}`} />
      <div className={`pr-tracker-dot${currentStep >= 3 || completedStep >= 3 ? " active" : ""}`} />
    </div>
    <ol className="pr-tracker-labels">
      {trackerStepLabels.map((label, i) => {
        const stepNumber = i + 1;
        const isActive = currentStep === stepNumber;
        const isComplete = completedStep >= stepNumber || currentStep > stepNumber;
        const status = isActive ? "current" : isComplete ? "complete" : "upcoming";
        return (
          <li
            key={i}
            className={`pr-tracker-label${isActive ? " active" : ""}`}
            aria-current={isActive ? "step" : undefined}
            aria-label={`${label} (${status})`}
          >
            {label}
          </li>
        );
      })}
    </ol>
  </div>
);

const SetupStakePay2 = ({ defaultStep }) => {
  const isInitialMount = useRef(true);

  const { isFetching: isFetchingConnectedAccounts, data: connectedAccounts } = useConnectedAccounts();
  const { isLoading: isLoadingDeals, data: deals } = useDeals();
  const { isLoading: isLoadingRentDetails, data: rentDetails } = useRentDetails();

  const { isFetching: isFetchingStakeDebit, data: stakeDebit } = useStakeDebit();

  const modalContext = useModalContext();
  const {
    modalStep,
    setModalClassName,
    setModalStep,
    setModalTitle,
    setModalPromoComponent,
    onModalClose,
    setModalStack,
    pushModalStack,
  } = modalContext;

  const rentAmount = useMemo(() => {
    return rentDetails?.data?.data?.my_home_rent?.rent_amount;
  }, [rentDetails]);

  const { isSavingAccount, plaidReady, openPlaid } = useConnectBankAccount({
    onError: onModalClose,
    onInitiatingSave: () => setModalClassName("setup-stake-pay-2"),
    onExit: () => setModalClassName("setup-stake-pay-2"),
  });

  const isBankConnected = useMemo(() => {
    return (connectedAccounts?.data?.data?.accounts?.length ?? 0) > 0;
  }, [connectedAccounts?.data?.data?.accounts?.length]);

  const [stakePay2SetupStatus, setStakePay2SetupStatus] = useState(defaultStep);

  const initialScreen = defaultStep === stakePay2SetupStatuses.steps1
      ? screenStates.landing
      : defaultStep === stakePay2SetupStatuses.steps2
        ? screenStates.step2
        : screenStates.step3;

  const [screen, setScreen] = useState(initialScreen);

  const IsSVDApproved = useMemo(() => isSVDApproved(stakeDebit?.data), [stakeDebit?.data]);

  const IsSVDDenied = useMemo(() => isSVDDenied(stakeDebit?.data), [stakeDebit?.data]);

  const handleStakePay2SetupStatusChange = (nextStep) => {
    setModalStep(Object.values(stakePay2SetupStatuses).findIndex((key) => key === nextStep) + 1);
  };

  useEffect(() => {
    setModalTitle("");
    setModalPromoComponent(null);
  }, [setModalTitle, setModalPromoComponent]);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    if (modalStep === 1) {
      setStakePay2SetupStatus(stakePay2SetupStatuses.steps1);
      setScreen(screenStates.step1);
    } else if (modalStep === 2) {
      setStakePay2SetupStatus(stakePay2SetupStatuses.steps2);
      setScreen(screenStates.step2);
    } else if (modalStep === 3) {
      setStakePay2SetupStatus(stakePay2SetupStatuses.steps3);
      setScreen(screenStates.step3);
    }
  }, [modalStep]);

  const handleAddCash = () => {
    setModalStack([]);
    setModalPromoComponent(null);
    pushModalStack(() => renderSetupStakePay2(modalContext, stakePay2SetupStatuses.steps2));
    renderGetBankAmountDetails(modalContext, {
      onSuccess: () => {
        setModalStack([]);
        renderSetupStakePay2(modalContext, stakePay2SetupStatuses.steps3);
      },
    });
  };

  const onRequestSVDCTAClick = () => {
    if (IsSVDDenied) {
      onModalClose();
    } else if (IsSVDApproved) {
      handleStakePay2SetupStatusChange(stakePay2SetupStatuses.steps2);
    } else {
      renderStakeCheckingOnboardingStep(modalContext)
    }
  };

  const getFirstIncompleteStep = () => {
    if (!IsSVDApproved) return { screen: screenStates.step1, step: 1, status: stakePay2SetupStatuses.steps1 };
    if (!isBankConnected || (stakeDebit?.data?.data?.available ?? 0) / 100 < rentAmount) {
      return { screen: screenStates.step2, step: 2, status: stakePay2SetupStatuses.steps2 };
    }
    return { screen: screenStates.step3, step: 3, status: stakePay2SetupStatuses.steps3 };
  };

  const handleGetStarted = () => {
    const target = getFirstIncompleteStep();
    setScreen(target.screen);
    setStakePay2SetupStatus(target.status);
    setModalStep(target.step);
  };

  const handleStepBack = () => {
    if (screen === screenStates.step1) {
      setScreen(screenStates.landing);
    } else if (screen === screenStates.step2) {
      setScreen(screenStates.step1);
      setModalStep(1);
    } else if (screen === screenStates.step3) {
      setScreen(screenStates.step2);
      setModalStep(2);
    }
  };

  const isLoadingAPIData =
    isLoadingDeals || isFetchingStakeDebit || isFetchingConnectedAccounts || isLoadingRentDetails;

  if (isLoadingAPIData) {
    return <PayRentSkeleton />;
  }

  if (screen === screenStates.landing) {
    return (
      <PayRentLanding
        onGetStarted={handleGetStarted}
        onClose={onModalClose}
        IsSVDApproved={IsSVDApproved}
        isBankConnected={isBankConnected}
        availableBalance={(stakeDebit?.data?.data?.available ?? 0) / 100}
        rentAmount={rentAmount}
      />
    );
  }

  return (
    <div className="pr-step-content">
      <StepHeader
        onBack={handleStepBack}
        onClose={onModalClose}
        backDisabled={false}
      />
      <Tracker currentStep={modalStep} completedStep={getFirstIncompleteStep().step - 1} />
      <div className="pr-divider" />

      {stakePay2SetupStatus === stakePay2SetupStatuses.steps1 && (
        <RequestSVD
          stakeDebit={stakeDebit}
          IsSVDApproved={IsSVDApproved}
          IsSVDDenied={IsSVDDenied}
          onPrimaryButtonClick={onRequestSVDCTAClick}
        />
      )}

      {stakePay2SetupStatus === stakePay2SetupStatuses.steps2 && (
        <DepositAmount
          onAddBank={() => {
            setModalClassName("d-none");
            openPlaid();
          }}
          onAddCash={handleAddCash}
          proceedToNextStep={handleStakePay2SetupStatusChange}
          isBankConnected={isBankConnected}
          availableBalance={(stakeDebit?.data?.data?.available ?? 0) / 100}
          rentAmount={rentAmount}
          isLoading={isSavingAccount || !plaidReady}
        />
      )}

      {stakePay2SetupStatus === stakePay2SetupStatuses.steps3 && (
        <UpdatePortal stakeDebit={stakeDebit} deals={deals} onComplete={onModalClose} />
      )}
    </div>
  );
};

const PayRentLanding = ({ onGetStarted, onClose, IsSVDApproved, isBankConnected, availableBalance, rentAmount }) => {
  const isBalanceEnough = availableBalance >= rentAmount;

  const getStepState = (stepNum) => {
    if (stepNum === 1) return IsSVDApproved ? "complete" : "active";
    if (stepNum === 2) return IsSVDApproved && isBankConnected && isBalanceEnough ? "complete" : (IsSVDApproved ? "active" : "inactive");
    return "inactive";
  };

  const steps = [
    { num: 1, name: "Open Stake Checking", desc: "Activate your free checking account" },
    { num: 2, name: "Add Funds", desc: "Transfer your rent amount into Stake" },
    { num: 3, name: "Update Payment Portal", desc: "Enter Stake account details for rent" },
  ];

  return (
    <div className="pr-landing">
      <div className="pr-landing-header">
        <CloseButton onClick={onClose} className="pr-close-btn" />
      </div>

      <div className="pr-landing-title-section">
        <h2 className="pr-landing-title">Pay Rent with Stake</h2>
        <p className="pr-landing-subtitle">
          Set up rent payments in 3 steps. No fees — processing fees refunded.
        </p>
      </div>

      <div className="pr-steps-list">
        {steps.map((step) => {
          const state = getStepState(step.num);
          return (
            <div key={step.num} className="pr-step-item">
              <div className={`pr-step-number ${state}`}>
                {state === "complete" ? "✓" : step.num}
              </div>
              <div className="pr-step-text">
                <span className="pr-step-name">{step.name}</span>
                <span className="pr-step-desc">{step.desc}</span>
              </div>
            </div>
          );
        })}
      </div>

      <div className="pr-landing-footer">
        <PrimaryButton onClick={onGetStarted}>Get Started</PrimaryButton>
      </div>
    </div>
  );
};

const RequestSVD = ({ stakeDebit, IsSVDApproved, IsSVDDenied, onPrimaryButtonClick }) => {
  const { width } = useViewport();

  const subtitle = useMemo(() => {
    const IsSVDEligible = isSVDEligible(stakeDebit?.data);
    const IsSVDPending = isSVDPending(stakeDebit?.data);
    const IsSVDRequested = isSVDCardRequested(stakeDebit?.data);
    const IsSVDDebitCardActivated = isSVDDebitCardActivated(stakeDebit?.data);

    if (IsSVDPending) {
      return "Your application is pending, additional steps are needed to continue.";
    } else if (IsSVDDebitCardActivated) {
      return "Your Stake Visa is ready.\nEarn cash back on every purchase.";
    } else if (IsSVDRequested) {
      return (
        <>
          Your card was requested! You <b>don't</b> need to wait for your card to arrive to continue.
        </>
      );
    } else if (IsSVDApproved) {
      return "Your Stake Visa Debit is approved and ready. Start earning Cash Back on your rent and everyday purchases.";
    } else if (IsSVDEligible) {
      return "Request your Stake Visa Debit card to earn extra Cash Back on rent.";
    } else {
      return null;
    }
  }, [stakeDebit, IsSVDApproved]);

  const title = useMemo(() => {
    if (IsSVDDenied) return "Unable to Pay Rent";
    if (IsSVDApproved) return "Open Stake Checking";
    return "Open Stake Checking";
  }, [IsSVDDenied, IsSVDApproved]);

  const ctaLabel = useMemo(() => {
    const IsSVDEligible = isSVDEligible(stakeDebit?.data);
    const IsSVDPending = isSVDPending(stakeDebit?.data);

    if (IsSVDEligible) {
      return "Request Card";
    } else if (IsSVDPending) {
      return "Continue";
    } else if (IsSVDDenied) {
      return "Return";
    } else if (IsSVDApproved) {
      return "Get Started";
    } else {
      return "Continue";
    }
  }, [stakeDebit, IsSVDDenied, IsSVDApproved]);

  const svdCardProgress = useMemo(() => {
    const IsSVDCardRequested = isSVDCardRequested(stakeDebit?.data);

    if (IsSVDCardRequested) {
      const physicalCardArrivesByDate = new Date(appendISOTime(stakeDebit?.data?.data?.physical_card_arrives_by));
      const formattedPhysicalCardArrivesByDate = formatDate(physicalCardArrivesByDate, nilYearLongMonthDateFormat);
      const HasSVDPhysicalCardArrived = hasSVDPhysicalCardArrived(physicalCardArrivesByDate);

      return (
        <CustomStepper
          className="my-sm-10 my-8 mx-auto"
          steps={[
            { label: "Approved", StepComponent: () => <div className="step-icon"></div> },
            {
              label: `Arrives by ${formattedPhysicalCardArrivesByDate}`,
              StepComponent: () => <div className="step-icon"></div>,
            },
            { label: "Call to activate your card", StepComponent: () => <div className="step-icon"></div> },
          ]}
          activeStep={!HasSVDPhysicalCardArrived ? 1 : 2}
          orientation={width > mediumScreenStartFrom ? "horizontal" : "vertical"}
          alternativeLabel={width > mediumScreenStartFrom ? true : false}
        />
      );
    } else {
      return null;
    }
  }, [stakeDebit, width]);

  const showCard = !isSVDCardRequested(stakeDebit?.data) && !IsSVDDenied;
  const IsSVDEligible = isSVDEligible(stakeDebit?.data);

  if (IsSVDApproved && showCard) {
    return (
      <div className="pr-approved-content">
        <div className="pr-title-section">
          <h2 className="pr-title">{title}</h2>
          {!!subtitle && <p className="pr-subtitle">{subtitle}</p>}
        </div>

        <div className="pr-card-preview">
          <img className="stake-card-front-side" src={StakeCardFrontSide} alt="" />
        </div>

        <div className="pr-footer">
          <PrimaryButton onClick={onPrimaryButtonClick}>{ctaLabel}</PrimaryButton>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="pr-title-section">
        <h2 className="pr-title">{title}</h2>
        {!!subtitle && <p className="pr-subtitle">{IsSVDDenied ? "Sorry, you are unable to pay rent with Stake Checking." : subtitle}</p>}
      </div>

      {IsSVDEligible && (
        <div className="pr-value-props">
          <div className="pr-value-prop">
            <GreenCheckIcon />
            <span>No monthly fees</span>
          </div>
          <div className="pr-value-prop">
            <GreenCheckIcon />
            <span>FDIC insured up to $250,000</span>
          </div>
          <div className="pr-value-prop">
            <GreenCheckIcon />
            <span>Earn Cash Back on rent payments</span>
          </div>
        </div>
      )}

      {showCard && !IsSVDEligible && (
        <div className="pr-card-preview">
          <img className="stake-card-front-side" src={StakeCardFrontSide} alt="" />
        </div>
      )}

      {svdCardProgress}

      <div className="pr-footer">
        <PrimaryButton onClick={onPrimaryButtonClick}>{ctaLabel}</PrimaryButton>
      </div>
    </>
  );
};

const DepositAmount = ({
  onAddBank,
  onAddCash,
  proceedToNextStep,
  isBankConnected,
  availableBalance,
  rentAmount,
  isLoading,
}) => {
  const isAmountEnough = useMemo(() => {
    return availableBalance >= rentAmount;
  }, [availableBalance, rentAmount]);

  const { subtitle, buttonLabel } = useMemo(() => {
    const defaultSubtitle =
      "Transfer your rent amount into your Stake account. Include utilities and fees.";

    if (!isBankConnected) {
      return {
        subtitle: "Transfer your rent amount into your Stake account. First, add a bank.",
        buttonLabel: "Add Bank",
      };
    } else if (!isAmountEnough) {
      return {
        subtitle: defaultSubtitle,
        buttonLabel: "Deposit Rent Amount",
      };
    } else {
      return {
        subtitle: defaultSubtitle,
        buttonLabel: "Continue",
      };
    }
  }, [isBankConnected, isAmountEnough]);

  return (
    <>
      <div className="pr-title-section">
        <h2 className="pr-title">Add Funds</h2>
        <p className="pr-subtitle">{subtitle}</p>
      </div>

      <div className="pr-balance-wrapper">
        <div className="pr-balance-card">
          <div className="pr-balance-info">
            <span className="pr-balance-label">Current Balance</span>
            <span className="pr-balance-amount">{formatCurrency(availableBalance)}</span>
          </div>
          <div className="pr-balance-actions">
            <button className="pr-balance-action-btn">Add Cash</button>
            <button className="pr-balance-action-btn">Cash Out</button>
          </div>
        </div>
      </div>

      {isBankConnected && !isAmountEnough && (
        <div className="pr-info-note">
          <span>Transfers take 1–2 business days to process.</span>
        </div>
      )}

      <div className="pr-footer">
        <PrimaryButton
          onClick={() => {
            if (!isBankConnected) {
              onAddBank();
            } else if (!isAmountEnough) {
              onAddCash();
            } else {
              proceedToNextStep(stakePay2SetupStatuses.steps3);
            }
          }}
          disabled={isLoading}
        >
          {buttonLabel} {isLoading && <CircularProgress />}
        </PrimaryButton>
        {isBankConnected && !isAmountEnough && (
          <button className="pr-skip-btn" onClick={() => proceedToNextStep(stakePay2SetupStatuses.steps3)}>
            Skip for now
          </button>
        )}
      </div>
    </>
  );
};

const UpdatePortal = ({ stakeDebit, deals, onComplete }) => {
  const portalLink = useMemo(() => {
    return deals?.data?.data?.currentDeal?.resident_payment_portal_link;
  }, [deals]);

  const { accountNumber, routingNumber, isStakePay25Enabled, stake25iFrameURL } = useMemo(() => {
    return {
      accountNumber: stakeDebit?.data?.data?.account_number,
      routingNumber: stakeDebit?.data?.data?.routing_number,
      isStakePay25Enabled: stakeDebit?.data?.data?.enabled_for_stakepay_2_5__c,
      stake25iFrameURL: stakeDebit?.data?.data?.stakepay_2_5_iframe_widget_url,
    };
  }, [stakeDebit]);

  if (isStakePay25Enabled) {
    return (
      <div className="pr-stakepay25-content">
        <div className="pr-title-section">
          <h2 className="pr-title">Set your payment schedule</h2>
        </div>
        <iframe
          src={stake25iFrameURL}
          title="Payment Schedule"
        />
        <div className="pr-info-note">
          <span>No fees. Processing fees are refunded.</span>
        </div>
        {!!portalLink && (
          <div className="pr-footer">
            <PrimaryButton onClick={onComplete}>Finish</PrimaryButton>
          </div>
        )}
      </div>
    );
  }

  return (
    <>
      <div className="pr-title-section">
        <h2 className="pr-title">Update Payment Portal</h2>
        <p className="pr-subtitle">Copy these numbers into your rent payment portal.</p>
      </div>

      <div className="pr-copy-fields">
        <div className="pr-copy-field">
          <div className="pr-copy-field-info">
            <span className="pr-copy-field-label">Account Number</span>
            <span className="pr-copy-field-value">{accountNumber}</span>
          </div>
          <CopyToClipboard
            text={accountNumber}
            onCopy={() => dispatchSuccessToast("Copied to clipboard", { key: "account_number" })}
          >
            <button className="pr-copy-btn">
              <CopyIcon />
              <span>Copy</span>
            </button>
          </CopyToClipboard>
        </div>
        <div className="pr-copy-field">
          <div className="pr-copy-field-info">
            <span className="pr-copy-field-label">Routing Number</span>
            <span className="pr-copy-field-value">{routingNumber}</span>
          </div>
          <CopyToClipboard
            text={routingNumber}
            onCopy={() => dispatchSuccessToast("Copied to clipboard", { key: "routing_number" })}
          >
            <button className="pr-copy-btn">
              <CopyIcon />
              <span>Copy</span>
            </button>
          </CopyToClipboard>
        </div>
      </div>

      <div className="pr-info-note">
        <span>No fees. Processing fees are refunded.</span>
      </div>

      <div className="pr-footer">
        {!!portalLink ? (
          <PrimaryButton onClick={() => window.open(portalLink, "_blank")}>Open portal</PrimaryButton>
        ) : (
          <PrimaryButton onClick={onComplete}>Done</PrimaryButton>
        )}
      </div>
    </>
  );
};
